// #region imports

// --- Angular
import { Component, OnInit, Output, Input, EventEmitter, ChangeDetectionStrategy, OnDestroy, SimpleChanges, OnChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';

// --- RxJs
import { Subscription } from 'rxjs';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

// --- HS Code
import { TermsAndConditionsModel, Constants } from '../../../models';

// #endregion

@Component({
  selector: 'hs-terms-and-conditions-form',
  templateUrl: './terms-and-conditions-form.component.html',
  styleUrls: ['./terms-and-conditions-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TermsAndConditionsFormComponent implements OnChanges, OnDestroy {
  @Input() errorMessage: string | null = '';
  @Input() isUpdating: boolean | null = false;
  @Input() showTerms: boolean | null = false;
  @Input() showEmailOpt: boolean | null = false;
  @Input() modal: NgbActiveModal | null = null;

  @Output() tacAccepted = new EventEmitter();
  @Output() userTermsAndConditionsUpdated = new EventEmitter<TermsAndConditionsModel>();
  @Output() userEmailOptInUpdated = new EventEmitter<TermsAndConditionsModel>();

  displayModes = {
    'default': 'Show the agreements form',
    'privacy': 'Show the privacy policy',
    'terms': 'Show the terms and conditions'
  };

  displayMode: string = this.displayModes.default;

  userAgreementLinks = {
    privacyLink: Constants.PRIVACY_POLICY_LINK,
    termsLink: Constants.TERMS_OF_AGREEMENT_LINK
  };

  termsForm: FormGroup;

  private _termsControlSubscription: Subscription = Subscription.EMPTY;
  private _emailOptInControlSubscription: Subscription = Subscription.EMPTY;

  constructor(
    private formBuilder: FormBuilder,
  ) {
    this.termsForm = this.formBuilder.group({
      terms: [false, Validators.required],
      emailOptIn: [false, Validators.nullValidator],
    });

    const termsControl = this.termsForm.get('terms');
    if (termsControl)
    {
      this._termsControlSubscription = termsControl.valueChanges
        .subscribe((selected: boolean) => {
          this.onUserTermsAndConditionsUpdated(selected);
      });
    }

    const emailOptInControl = this.termsForm.get('emailOptIn');
    if (emailOptInControl) {
      this._emailOptInControlSubscription = emailOptInControl.valueChanges
      .subscribe((selected: boolean) => {
        this.onUserEmailOptInUpdated(selected);
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      if (changes['showTerms'] && changes['showTerms'].currentValue) {
        this._updateTermsControl(changes['showTerms'].currentValue);
      }
      if (changes['showEmailOpt'] && changes['showEmailOpt'].currentValue) {
        this._updateEmailOptInControl(changes['showEmailOpt'].currentValue);
      }
    }
  }

  ngOnDestroy() {
    if (this._termsControlSubscription) {
      this._termsControlSubscription.unsubscribe();
      this._termsControlSubscription = Subscription.EMPTY;
    }
    if (this._emailOptInControlSubscription) {
      this._emailOptInControlSubscription.unsubscribe();
      this._emailOptInControlSubscription = Subscription.EMPTY;
    }
  }

  private _updateTermsControl(selected: boolean) {
    const termsControl = this.termsForm.get('terms');
    if (termsControl) {
      termsControl.setValue(selected, { emitEvent: false });
    }
  }

  private _updateEmailOptInControl(selected: boolean) {
    const emailOptInControl = this.termsForm.get('emailOptIn');
    if (emailOptInControl) {
      emailOptInControl.setValue(selected, { emitEvent: false });
    }
  }

  acceptClick(): void {
    if (!this.termsForm.valid) {
      return;
    }

    this.tacAccepted.emit();
  }

  onShowTermsAndConditionsclicked() {
    this.displayMode = this.displayModes.terms;
  }

  onShowPrivacyPolicyClicked() {
    this.displayMode = this.displayModes.privacy;
  }

  onUserTermsAndConditionsUpdated(selected: boolean): void {
    const model = { hasAcceptedTerms: selected } as TermsAndConditionsModel;
    this.userTermsAndConditionsUpdated.emit(model);
  }

  onUserEmailOptInUpdated(selected: boolean): void {
    const model = { emailOptIn: selected } as TermsAndConditionsModel;
    this.userEmailOptInUpdated.emit(model);
  }

  onBackClicked() {
    this.displayMode = this.displayModes.default;
  }

}
